import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { MenuItem, Tab, Tabs, Tooltip } from '@mui/material';
import { SxProps } from '@mui/system';
import { DropdownMenu } from 'framework/ui/menu';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

type TabMenuItemType = {
  label: string;
  isVisible?: boolean;
  disabled?: boolean;
  disabledReason?: string | JSX.Element | null;
  menuAction: () => void;
};

export type TabItem = {
  value?: StringOrNumber;
  label: React.ReactNode;
  isVisible?: boolean;
  selected?: boolean;
  content?: React.ReactNode;
  onClick?: () => void;
  menuIcon?: React.ReactNode;
  menuItems?: TabMenuItemType[];
  sxTab?: SxProps;
  [Key: string]: any;
};

type TabsWrapperProps = {
  tabs: TabItem[];
  sxContentProps?: SxProps;
  sxTab?: SxProps;
  sxTabs?: SxProps;
  defaultTab?: StringOrNumber;
  buttons?: React.ReactNode[];
  buttonsPlacement?: 'start' | 'end';
  changeTabCallback?: (index: number) => void;
  isShowContent?: boolean;
  isAtroScrollEnable?: boolean;
  wrapButtons?: boolean;
};

const CustomTab: React.FC<TabItem> = ({ label, menuItems, sxTab, selected, content, ...props }) => {
  const visibleMenuItems = menuItems?.filter(item => item.isVisible !== false);

  return (
    <Box>
      <Tab
        component="div"
        sx={{ textTransform: 'none', ...sxTab }}
        label={label}
        selected={selected}
        iconPosition="start"
        {...(props as any)}
      />
      {visibleMenuItems?.length ? (
        <DropdownMenu
          popupId="dropdown-tab-id"
          iconButton={
            <IconButton size="small" sx={!selected ? { visibility: 'hidden', marginLeft: 0.5 } : { marginLeft: 0.5 }}>
              <KeyboardArrowDownIcon />
            </IconButton>
          }
        >
          {visibleMenuItems.map(item => {
            const menuItem = (
              <MenuItem key={`${item.label}-${label}`} disabled={item.disabled} onClick={item.menuAction}>
                {item.label}
              </MenuItem>
            );

            if (item.disabled && item.disabledReason) {
              return (
                <Tooltip title={item.disabledReason}>
                  <span>{menuItem}</span>
                </Tooltip>
              );
            } else {
              return menuItem;
            }
          })}
        </DropdownMenu>
      ) : null}
    </Box>
  );
};

const TabsWrapper: React.FC<TabsWrapperProps> = ({
  tabs,
  sxTab,
  sxContentProps,
  sxTabs,
  buttons = [],
  buttonsPlacement = 'start',
  defaultTab = 0,
  changeTabCallback,
  isShowContent = true,
  isAtroScrollEnable = false,
  wrapButtons = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const visibleTabs = tabs
    ?.filter(item => item?.isVisible !== false)
    .map((item, index) => ({
      ...item,
      value: item.value || index,
    }));

  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
      changeTabCallback?.(newValue);
    },
    [changeTabCallback],
  );

  useEffect(() => {
    if (isAtroScrollEnable) {
      const container = scrollRef.current;
      const activeTabElemnt = container?.querySelector('.Mui-selected');

      if (container && activeTabElemnt) {
        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTabElemnt.getBoundingClientRect();

        // Аuro scroll to active elemnt
        container.scrollLeft += tabRect.left - containerRect.left;
      }
    }
  }, [isAtroScrollEnable]);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        minWidth={0}
        flexWrap={wrapButtons ? 'wrap' : 'nowrap'}
        useFlexGap={wrapButtons}
        rowGap={wrapButtons ? 1 : 0}
      >
        <Tabs
          ref={scrollRef}
          sx={{
            flex: buttonsPlacement === 'end' ? '1 1 auto' : '0 1 auto',
            minWidth: 0,
            flexWrap: 'wrap',
            overflow: 'visible',
            '> .MuiTabs-fixed': {
              overflow: 'visible !important',
            },
            ...sxTabs,
          }}
          value={activeTab}
          onChange={handleTabChange}
          aria-label="TabsWrapper"
        >
          {visibleTabs.map((tab, index: number) => (
            <CustomTab key={tab.value || index} sxTab={sxTab} {...tab} />
          ))}
        </Tabs>
        {buttons.length ? (
          <Box
            sx={{
              flexShrink: wrapButtons ? 1 : 0,
              minWidth: wrapButtons ? 0 : undefined,
              maxWidth: wrapButtons ? '100%' : undefined,
              ml: buttonsPlacement === 'end' ? 'auto' : 0,
            }}
          >
            {buttons}
          </Box>
        ) : null}
      </Stack>

      {isShowContent && (
        <Box sx={sxContentProps || { padding: 2 }}>
          {visibleTabs.map(tab =>
            tab.value === activeTab ? (
              <Box key={activeTab} sx={{ paddingTop: 2 }}>
                {tab.content}
              </Box>
            ) : null,
          )}
        </Box>
      )}
    </Box>
  );
};

export default TabsWrapper;
